
export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  condition: (score: number, totalQuestions: number, difficulty: string) => boolean;
  category: 'mastery' | 'exploration' | 'speed' | 'streak' | 'perfection' | 'challenge' | 'coding' | 'flashcard';
  points: number;
  targetValue: number;
  currentValue?: number;
}

export interface UserAchievements {
  [achievementId: string]: {
    earned: boolean;
    earnedAt?: string;
    progress?: number;
  };
}

export const achievements: Achievement[] = [
  {
    id: "speed_demon",
    title: "Speed Demon",
    description: "Complete a quiz with a score of 90% or higher",
    icon: "⚡",
    condition: (score, totalQuestions) => {
      const percentage = (score / totalQuestions) * 100;
      console.log('Speed Demon check - Score:', score, 'Total:', totalQuestions, 'Percentage:', percentage);
      return percentage >= 90;
    },
    category: 'speed',
    points: 500,
    targetValue: 90
  },
  {
    id: "perfect_streak",
    title: "Perfect Streak",
    description: "Complete 5 quizzes in a row with scores above 80%",
    icon: "🔥",
    condition: (score, totalQuestions) => {
      const percentage = (score / totalQuestions) * 100;
      console.log('Perfect Streak check - Score:', score, 'Total:', totalQuestions, 'Percentage:', percentage);
      return percentage >= 80;
    },
    category: 'streak',
    points: 300,
    targetValue: 5
  },
  {
    id: "subject_explorer",
    title: "Subject Explorer",
    description: "Score above 70% in 3 different subjects",
    icon: "🧭",
    condition: (score, totalQuestions) => {
      const percentage = (score / totalQuestions) * 100;
      console.log('Subject Explorer check - Score:', score, 'Total:', totalQuestions, 'Percentage:', percentage);
      return percentage >= 70;
    },
    category: 'exploration',
    points: 200,
    targetValue: 3
  },
  {
    id: "master_mind",
    title: "Master Mind",
    description: "Achieve a perfect score in any difficulty level",
    icon: "🎓",
    condition: (score, totalQuestions) => {
      const isPerfect = score === totalQuestions;
      console.log('Master Mind check - Score:', score, 'Total:', totalQuestions, 'Perfect:', isPerfect);
      return isPerfect;
    },
    category: 'mastery',
    points: 1000,
    targetValue: 100
  },
  {
    id: "challenge_seeker",
    title: "Challenge Seeker",
    description: "Score above 75% in a hard difficulty quiz",
    icon: "🏆",
    condition: (score, totalQuestions, difficulty) => {
      const percentage = (score / totalQuestions) * 100;
      console.log('Challenge Seeker check - Score:', score, 'Total:', totalQuestions, 'Percentage:', percentage, 'Difficulty:', difficulty);
      return percentage >= 75 && difficulty === "hard";
    },
    category: 'challenge',
    points: 750,
    targetValue: 5
  },
  {
    id: "flawless_victory",
    title: "Flawless Victory",
    description: "Answer all questions correctly in any quiz",
    icon: "💫",
    condition: (score, totalQuestions) => {
      const isPerfect = score === totalQuestions;
      console.log('Flawless Victory check - Score:', score, 'Total:', totalQuestions, 'Perfect:', isPerfect);
      return isPerfect;
    },
    category: 'perfection',
    points: 400,
    targetValue: 100
  },
  // New achievements for difficulty levels
  {
    id: "easy_master",
    title: "Easy Master",
    description: "Complete an easy difficulty quiz",
    icon: "🟢",
    condition: (score, totalQuestions, difficulty) => {
      const percentage = (score / totalQuestions) * 100;
      console.log('Easy Master check - Score:', score, 'Difficulty:', difficulty);
      return percentage >= 60 && difficulty === "easy";
    },
    category: 'mastery',
    points: 5,
    targetValue: 1
  },
  {
    id: "medium_achiever",
    title: "Medium Achiever",
    description: "Complete a medium difficulty quiz",
    icon: "🟡",
    condition: (score, totalQuestions, difficulty) => {
      const percentage = (score / totalQuestions) * 100;
      console.log('Medium Achiever check - Score:', score, 'Difficulty:', difficulty);
      return percentage >= 60 && difficulty === "medium";
    },
    category: 'mastery',
    points: 10,
    targetValue: 1
  },
  {
    id: "hard_conqueror",
    title: "Hard Conqueror",
    description: "Complete a hard difficulty quiz",
    icon: "🔴",
    condition: (score, totalQuestions, difficulty) => {
      const percentage = (score / totalQuestions) * 100;
      console.log('Hard Conqueror check - Score:', score, 'Difficulty:', difficulty);
      return percentage >= 60 && difficulty === "hard";
    },
    category: 'mastery',
    points: 20,
    targetValue: 1
  },
  // Coding playground achievements
  {
    id: "code_master",
    title: "Code Master",
    description: "Complete a coding challenge successfully",
    icon: "💻",
    condition: () => true, // This will need to be hooked up to the coding playground logic
    category: 'coding',
    points: 20,
    targetValue: 1
  },
  {
    id: "code_genius",
    title: "Code Genius",
    description: "Complete 5 coding challenges",
    icon: "🧠",
    condition: () => true, // This will need to be hooked up to the coding playground logic
    category: 'coding',
    points: 100,
    targetValue: 5
  },
  // Flashcard achievements
  {
    id: "memory_master",
    title: "Memory Master",
    description: "Review 10 flashcards in a session",
    icon: "🃏",
    condition: () => true, // This will need to be hooked up to the flashcard system
    category: 'flashcard',
    points: 10,
    targetValue: 10
  },
  {
    id: "flashcard_guru",
    title: "Flashcard Guru",
    description: "Create a deck with at least 20 flashcards",
    icon: "📚",
    condition: () => true, // This will need to be hooked up to the flashcard system
    category: 'flashcard',
    points: 20,
    targetValue: 20
  }
];
